import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import {
  Close,
  ContentCopy,
  OpenInNew,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";

const BRAND = "#6D28D9";

function MetaRow({ label, value, boxed }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      sx={{ py: 1.1, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: { sm: 160 }, fontWeight: 600 }}
      >
        {label}
      </Typography>
      {boxed ? (
        <Box
          sx={{
            flex: 1,
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
            border: "1px solid",
            borderColor: "divider",
            wordBreak: "break-word",
            whiteSpace: "pre-wrap",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {value || "—"}
        </Box>
      ) : (
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, wordBreak: "break-word" }}>
          {value || "—"}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Generation Monitor job detail — media preview + settings like the DAI Studio mock.
 */
export default function GenerationJobDetailModal({ open, jobId, onClose, lang }) {
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [loading, setLoading] = React.useState(false);
  const [detail, setDetail] = React.useState(null);

  React.useEffect(() => {
    if (!open || !jobId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setDetail(null);
      try {
        const res = await hitAxios({
          path: `/api/admin/generation_job/${encodeURIComponent(jobId)}`,
          post: false,
          admin: true,
          showLoading: false,
          showSnackbar: false,
        });
        if (cancelled) return;
        if (res?.data?.success) setDetail(res.data.data);
        else showSnack?.(res?.data?.msg || "Could not load job", false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, jobId, hitAxios, showSnack]);

  const mediaUrl = detail?.media_url || "";
  const looksVideo = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(mediaUrl);
  const isVideo =
    detail?.media_type === "video" || (detail?.media_type !== "image" && looksVideo);
  const isImage = Boolean(mediaUrl) && !isVideo;

  const copyUrl = async () => {
    if (!mediaUrl) return;
    try {
      await navigator.clipboard.writeText(mediaUrl);
      showSnack?.(lang?.copied || "Copied", true);
    } catch {
      showSnack?.("Could not copy", false);
    }
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
        {lang?.generationResultTitle || "Generation Result"}
        {jobId ? ` — #${String(jobId).slice(0, 12)}` : ""}
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ py: 6, display: "grid", placeItems: "center" }}>
            <CircularProgress size={32} />
          </Box>
        ) : !detail ? (
          <Typography color="text.secondary">No details available.</Typography>
        ) : (
          <Stack spacing={2.5}>
            {/* Media preview */}
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "#0b0b0b",
                border: "1px solid",
                borderColor: "divider",
                minHeight: 180,
                display: "grid",
                placeItems: "center",
              }}
            >
              {mediaUrl && isVideo ? (
                <Box
                  component="video"
                  src={mediaUrl}
                  controls
                  playsInline
                  sx={{ width: "100%", maxHeight: 420, display: "block", bgcolor: "#000" }}
                />
              ) : mediaUrl && isImage ? (
                <Box
                  component="img"
                  src={mediaUrl}
                  alt="Generated"
                  sx={{
                    width: "100%",
                    maxHeight: 420,
                    objectFit: "contain",
                    display: "block",
                    bgcolor: "#111",
                  }}
                />
              ) : (
                <Typography color="grey.500" sx={{ py: 6 }}>
                  {lang?.noMediaYet || "No media available yet for this job."}
                </Typography>
              )}
            </Box>

            {mediaUrl ? (
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNew />}
                  href={mediaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: "none", fontWeight: 700, alignSelf: "center" }}
                >
                  {lang?.openInNewTab || "Open in new tab"}
                </Button>
                <TextField
                  size="small"
                  fullWidth
                  value={mediaUrl}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={copyUrl} edge="end" size="small">
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            ) : null}

            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                label={(detail.media_type || detail.studio || "").toUpperCase()}
                sx={{ bgcolor: BRAND, color: "#fff", fontWeight: 800 }}
              />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {detail.feature || "—"}
              </Typography>
            </Stack>

            <Box>
              <Typography
                variant="caption"
                fontWeight={800}
                color="text.secondary"
                sx={{ letterSpacing: 0.8 }}
              >
                GENERATION SETTINGS
              </Typography>
              <Box sx={{ mt: 1 }}>
                {(detail.settings || []).map((row) => (
                  <MetaRow key={row.label} label={row.label} value={row.value} boxed={row.boxed} />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography
                variant="caption"
                fontWeight={800}
                color="text.secondary"
                sx={{ letterSpacing: 0.8 }}
              >
                REQUEST INPUT
              </Typography>
              <Box sx={{ mt: 1 }}>
                {(detail.request_input || []).map((row) => (
                  <MetaRow
                    key={`req-${row.label}`}
                    label={row.label}
                    value={row.value}
                    boxed={row.boxed}
                  />
                ))}
              </Box>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none", fontWeight: 700 }}>
          {lang?.close || "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
